import React, { createContext, useContext, useState, useCallback } from "react";
import MasterPasswordDialog from "./index";

const MasterPasswordContext = createContext();

export const useMasterPassword = () => {
    const context = useContext(MasterPasswordContext);
    if (!context) {
        throw new Error(
            "useMasterPassword must be used within a MasterPasswordProvider"
        );
    }
    return context;
};

export const MasterPasswordProvider = ({ children }) => {
    const [dialogState, setDialogState] = useState({
        open: false,
        title: "Master Password Required",
        message:
            "Please enter the master password to proceed with elevated privileges:",
        elevatedText: "",
        resolve: null,
        reject: null,
        loading: false,
    });

    const showMasterPasswordDialog = useCallback((options = {}) => {
        return new Promise((resolve, reject) => {
            setDialogState({
                open: true,
                title: options.title || "Master Password Required",
                message:
                    options.message ||
                    "Please enter the master password to proceed with elevated privileges:",
                elevatedText: options.elevatedText || "",
                resolve,
                reject,
                loading: false,
            });
        });
    }, []);

    const handleConfirm = useCallback(
        async (password) => {
            setDialogState((prev) => ({ ...prev, loading: true }));

            try {
                // Resolve the promise with the password
                if (dialogState.resolve) {
                    dialogState.resolve(password);
                }

                // Close dialog
                setDialogState((prev) => ({
                    ...prev,
                    open: false,
                    loading: false,
                    resolve: null,
                    reject: null,
                }));
            } catch (error) {
                setDialogState((prev) => ({ ...prev, loading: false }));
                throw error;
            }
        },
        [dialogState]
    );

    const handleClose = useCallback(() => {
        if (dialogState.loading) return;

        // Reject the promise
        if (dialogState.reject) {
            dialogState.reject(new Error("Master password dialog cancelled"));
        }

        setDialogState((prev) => ({
            ...prev,
            open: false,
            resolve: null,
            reject: null,
            elevatedText: "",
        }));
    }, [dialogState]);

    const contextValue = {
        showMasterPasswordDialog,
    };

    return (
        <MasterPasswordContext.Provider value={contextValue}>
            {children}
            <MasterPasswordDialog
                open={dialogState.open}
                onConfirm={handleConfirm}
                onClose={handleClose}
                title={dialogState.title}
                message={dialogState.message}
                elevatedText={dialogState.elevatedText}
                loading={dialogState.loading}
            />
        </MasterPasswordContext.Provider>
    );
};
